package org.teddapp.project.ui.portfolio

import androidx.compose.foundation.background
import org.teddapp.project.ui.TeddMenuEntry
import org.teddapp.project.ui.TeddMenu
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.teddapp.project.portfolio.CompetenceLevel
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.tierColor
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddTagTone
import org.teddapp.project.ui.teddClickable
import androidx.compose.foundation.shape.CircleShape

/** Couleur d'un niveau : celle du palier correspondant. */
@Composable
internal fun levelColor(level: CompetenceLevel): Color = tierColor(level.tier)

internal fun parseHex(hex: String?, fallback: Color): Color =
    runCatching { Color(("FF" + hex.orEmpty().trimStart('#')).toLong(16)) }.getOrElse { fallback }

/** Étiquette de niveau : le mot, précédé d'un point à la couleur du niveau. */
@Composable
internal fun LevelBadge(level: CompetenceLevel?, modifier: Modifier = Modifier, @Suppress("UNUSED_PARAMETER") small: Boolean = false) {
    TeddTag(
        text = level?.label ?: "À évaluer",
        modifier = modifier,
        tone = if (level == null) TeddTagTone.Outline else TeddTagTone.Neutral,
        dot = level != null,
        dotColor = level?.let { levelColor(it) },
    )
}

/** Répartition des critères par niveau, en barre segmentée ; vide = trait discret. */
@Composable
internal fun LevelDistributionBar(distribution: IntArray, modifier: Modifier = Modifier, height: Dp = 6.dp) {
    val total = distribution.sum()
    Row(
        modifier = modifier.fillMaxWidth().height(height).clip(RoundedCornerShape(height / 2)).background(TeddTokens.SurfaceSunken),
        horizontalArrangement = Arrangement.spacedBy(1.dp),
    ) {
        if (total == 0) return@Row
        // Du plus haut au plus bas : ce qu'on maîtrise se lit en premier.
        for (i in 3 downTo 0) {
            val n = distribution[i]
            if (n > 0) Box(modifier = Modifier.weight(n.toFloat()).height(height).background(levelColor(CompetenceLevel.of(i))))
        }
    }
}

/** Les quatre niveaux à choisir d'un tap. Le niveau courant se pose sur la surface, cerclé d'encre. */
@Composable
internal fun LevelPicker(level: CompetenceLevel, onChange: (CompetenceLevel) -> Unit, modifier: Modifier = Modifier, dense: Boolean = false) {
    val shape = RoundedCornerShape(TeddTokens.RFull)
    Row(modifier = modifier, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
        CompetenceLevel.entries.forEach { l ->
            val on = l == level
            Row(
                modifier = Modifier
                    .border(1.dp, if (on) TeddTokens.Ink else TeddTokens.Line, shape)
                    .teddClickable(onClick = { onChange(l) }, shape = shape, fill = if (on) TeddTokens.Surface else Color.Transparent)
                    .height(28.dp)
                    .padding(horizontal = if (dense) 8.dp else 10.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                Box(modifier = Modifier.size(8.dp).background(levelColor(l), CircleShape))
                Text(
                    text = if (dense) l.short else l.label,
                    color = if (on) TeddTokens.Ink else TeddTokens.InkMuted,
                    style = TeddType.Caption.copy(fontWeight = FontWeight(if (on) 600 else 450)),
                    maxLines = 1,
                )
            }
        }
    }
}

@Composable
internal fun PfEyebrow(text: String, modifier: Modifier = Modifier) {
    Text(text.uppercase(), style = TeddType.Overline, color = TeddTokens.InkMuted, modifier = modifier)
}

/** Ligne discrète de méta-donnée : icône + texte. */
@Composable
internal fun PfMetaRow(icon: String, text: String, modifier: Modifier = Modifier) {
    Row(modifier = modifier, verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
        TeddIcon(name = icon, size = 12.dp, color = TeddTokens.InkMuted)
        Text(text, color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 2, overflow = TextOverflow.Ellipsis)
    }
}

/** Sélecteur simple : un champ qui ouvre un menu. options = (valeur, libellé) ; valeur null = « aucun ». */
@Composable
internal fun PfSelect(
    label: String,
    options: List<Pair<String?, String>>,
    selected: String?,
    onSelect: (String?) -> Unit,
    modifier: Modifier = Modifier,
    placeholder: String = "Aucun",
) {
    var open by remember { mutableStateOf(false) }
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Column(modifier = modifier) {
        PfEyebrow(label, modifier = Modifier.padding(bottom = 6.dp, start = 2.dp))
        Box {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(shape)
                    .background(TeddTokens.Surface, shape)
                    .border(1.dp, TeddTokens.Line, shape)
                    .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null) { open = true }
                    .padding(horizontal = 12.dp, vertical = 11.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                val current = options.firstOrNull { it.first == selected }?.second
                Text(
                    text = current ?: placeholder,
                    color = if (current != null) TeddTokens.Ink else TeddTokens.InkMuted,
                    fontSize = 13.sp,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.weight(1f),
                )
                TeddIcon(name = "chevron", size = 12.dp, color = TeddTokens.InkMuted)
            }
            TeddMenu(
                expanded = open,
                onDismiss = { open = false },
                entries = options.map { (value, text) -> TeddMenuEntry(text, selected = value == selected, onClick = { onSelect(value) }) },
            )
        }
    }
}

/** Petit carré coloré pour une famille. */
@Composable
internal fun FamilyDot(color: String, icon: String, size: Dp = 22.dp) {
    val c = parseHex(color, TeddTokens.LimeStrong)
    Box(
        modifier = Modifier.size(size).clip(RoundedCornerShape(size / 3)).background(c.copy(alpha = 0.18f), RoundedCornerShape(size / 3)),
        contentAlignment = Alignment.Center,
    ) {
        TeddIcon(name = icon, size = size / 2, color = c, strokeWidth = 2.dp)
    }
}

internal val FAMILY_PALETTE = listOf("#22508A", "#2D7A4F", "#9A6B3A", "#6F7D8C", "#B8892C", "#0F7C8C", "#7C3AED", "#BE123C")
internal val FAMILY_ICONS = listOf("book", "cards", "focus", "target", "spark", "grid", "flag", "users", "wallet", "heart")
